import React, { useState } from "react";
import { toast } from "react-toastify";
import { MultiSelect } from "primereact/multiselect";
import * as platformService from "../../../services/platformService";
import useFinOptions from "../../Fin/useFinOptions";
import PermissionCoverage, { countHolders } from "../../../components/PermissionCoverage";
import { EmptyState, Field, FormDrawer, LoadState, PrimaryButton, cardStyle, iconBtnStyle, tdStyle, thStyle, useLoad } from "../../../components/ui";

// Who can do what in Finance / the Dealership: permission sets given to HR employees,
// optionally only for some business units or locations. Only employees can be chosen
// (add the person in HR > Employees first); the owner and admins have everything.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const multiStyle = { width: "100%", minHeight: 40, fontSize: 14 };

// showScopes: Finance business units / locations only exist where Finance is on; Procurement can hide the pickers.
export default function AccessSetup({ module, label, showScopes = true }) {
  const { data, loading, error, reload } = useLoad(() => platformService.listPermissions(module), [module]);
  const catalog = useLoad(() => platformService.getPermissionCatalog(module), [module]);
  const opts = useFinOptions(["business-units", "locations"]);
  const [adding, setAdding] = useState(false);
  const rows = data || [];
  const permissions = (catalog.data || []).map((p) => ({ label: p.label, value: p.value, description: p.description }));

  const revoke = async (g) => {
    const res = await platformService.revokePermission(module, g.id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || "Removed");
      reload();
    } else toast.error(platformService.responseMessage(res));
  };

  return (
    <>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
        The account owner and admins always have every permission. Everyone else needs a permission set here, and must be an active employee in HR.
      </div>
      {!loading && !error && !catalog.loading ? <PermissionCoverage permissions={permissions} holders={countHolders(rows)} /> : null}
      <div className="mb-3">
        <PrimaryButton onClick={() => setAdding(true)}>Give a permission</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No permissions given" text={`Give your team access to ${label}.`} />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {["Employee", "Permission", "Where", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.id}>
                    <td style={tdStyle}>
                      {g.employee_name || "—"} <span style={{ color: "#6B7280", fontSize: 12 }}>{g.employee_code}</span>
                      {g.employee_status === "SEPARATED" ? <div style={{ fontSize: 12, color: "#991B1B" }}>Left the company – this access no longer works</div> : null}
                    </td>
                    <td style={tdStyle}>{g.permission_label}</td>
                    <td style={tdStyle}>{g.scopes.length ? g.scopes.map((s) => s.name).filter(Boolean).join(", ") : "Whole company"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => revoke(g)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={adding}
        title="Give a permission"
        initial={{ bu_ids: [], location_ids: [] }}
        onHide={() => setAdding(false)}
        onSave={(clean, raw) =>
          platformService.grantPermission(module, {
            employee_id: clean.employee_id,
            permission: clean.permission,
            scopes: [...(raw.bu_ids || []).map((id) => ({ scope_type: "BU", scope_id: id })), ...(raw.location_ids || []).map((id) => ({ scope_type: "LOCATION", scope_id: id }))],
          })
        }
        onDone={reload}
        fields={[
          { key: "employee_id", label: "Employee", type: "employee", required: true },
          { key: "permission", label: "Permission", type: "select", required: true, options: permissions },
        ]}
      >
        {(values, setValues) => (
          <>
            {showScopes ? (
              <>
                <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 8 }}>Leave both empty to give access to the whole company.</div>
                <Field label="Only these business units" htmlFor="acc-bu">
                  <MultiSelect inputId="acc-bu" value={values.bu_ids || []} options={opts["business-units"] || []} onChange={(e) => setValues((s) => ({ ...s, bu_ids: e.value }))} placeholder="All business units" display="chip" style={multiStyle} />
                </Field>
                <Field label="Only these locations" htmlFor="acc-loc">
                  <MultiSelect inputId="acc-loc" value={values.location_ids || []} options={opts.locations || []} onChange={(e) => setValues((s) => ({ ...s, location_ids: e.value }))} placeholder="All locations" display="chip" style={multiStyle} />
                </Field>
              </>
            ) : null}
          </>
        )}
      </FormDrawer>
    </>
  );
}
